/*
  Measures one announcement, fills the bar with enough copies to cover it, and
  hands the CSS a travel distance (one group width) plus a duration derived from
  the speed set in the theme editor. Re-runs when the bar is resized or when the
  chosen webfont finishes loading, since either changes the measurement.
*/
class AnnouncementMarquee extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.announcement-bar__track');
    this.group = this.querySelector('.announcement-bar__group');
    this.lastWidth = null;
    this.frame = null;
    this.onResize = this.onResize.bind(this);
  }

  connectedCallback() {
    if (!this.track || !this.group) return;

    this.render();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.lastWidth = null;
        this.render();
      });
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.onResize);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  onResize() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.render());
  }

  render() {
    const containerWidth = this.getBoundingClientRect().width;
    if (!containerWidth || containerWidth === this.lastWidth) return;
    this.lastWidth = containerWidth;

    this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());

    const groupWidth = this.group.getBoundingClientRect().width;
    if (!groupWidth) return;

    const speed = parseFloat(this.dataset.speed) || 48;
    const copies = Math.ceil(containerWidth / groupWidth) + 1;
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < copies; i++) {
      const clone = this.group.cloneNode(true);
      clone.setAttribute('data-marquee-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      // Repeats are decoration: keep them out of the tab order.
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      fragment.appendChild(clone);
    }

    this.track.appendChild(fragment);
    this.style.setProperty('--announcement-marquee-distance', `${groupWidth}px`);
    this.style.setProperty('--announcement-marquee-duration', `${groupWidth / speed}s`);
    this.setAttribute('data-marquee-ready', '');
  }
}

if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', AnnouncementMarquee);
}
